/* eslint-disable max-lines */
import { OccupancyBucket } from '@hakimo-ui/hakimo/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMemo } from 'react';

dayjs.extend(utc);

interface Props {
  occupancy: OccupancyBucket[];
  /** A limit to draw across the occupancy row, e.g. a max_occupancy count */
  threshold?: number | null;
}

const WIDTH = 720;
const OCC_HEIGHT = 120;
const GUTTER_LEFT = 28;
const GUTTER_RIGHT = 8;
const AXIS_HEIGHT = 18;
/** Roughly what "HH:mm" spans at 10px, used to keep labels inside the svg */
const LABEL_WIDTH = 30;

/**
 * Occupancy over time, as a step function.
 *
 * Buckets with nothing observed are drawn as gaps, never as zero: the camera
 * only uploads when something moves.
 */
export function OccupancyChart({ occupancy, threshold }: Props) {
  const plotWidth = WIDTH - GUTTER_LEFT - GUTTER_RIGHT;
  const n = occupancy.length;
  const slot = n > 0 ? plotWidth / n : plotWidth;

  const yMax = useMemo(() => {
    const top = Math.max(1, ...occupancy.map((b) => b.max), threshold ?? 0);
    return Math.ceil(top * 1.15);
  }, [occupancy, threshold]);

  const yFor = (value: number) =>
    OCC_HEIGHT - (value / yMax) * (OCC_HEIGHT - 6);

  // Bucket times arrive as naive ISO strings in UTC (no offset), so parse
  // them as UTC explicitly -- dayjs(iso) would read them as local -- and
  // show them in the viewer's own timezone.
  const fmt = (iso: string, format: string) =>
    dayjs.utc(iso).local().format(format);

  // One label per ~90px so they never collide.
  const labelEvery = Math.max(1, Math.ceil(90 / slot));

  const stepPath = useMemo(() => {
    let d = '';
    let open = false;
    occupancy.forEach((b, i) => {
      const x0 = GUTTER_LEFT + i * slot;
      const x1 = x0 + slot;
      if (b.observedSeconds === 0) {
        open = false;
        return;
      }
      const y = yFor(b.max);
      d += open ? ` H${x0} V${y} H${x1}` : ` M${x0},${y} H${x1}`;
      open = true;
    });
    return d.trim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occupancy, slot, yMax]);

  const totalHeight = OCC_HEIGHT + AXIS_HEIGHT;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${totalHeight}`}
      className="h-auto w-full"
      role="img"
      aria-label="Occupancy over time"
    >
      {/* grid */}
      {[0.5, 1].map((f) => (
        <line
          key={f}
          x1={GUTTER_LEFT}
          x2={WIDTH - GUTTER_RIGHT}
          y1={yFor(yMax * f)}
          y2={yFor(yMax * f)}
          className="stroke-onlight-line-2 dark:stroke-ondark-line-2"
          strokeWidth={1}
        />
      ))}
      <text
        x={GUTTER_LEFT - 6}
        y={yFor(yMax) + 4}
        textAnchor="end"
        className="fill-onlight-text-3 dark:fill-ondark-text-3"
        fontSize={10}
      >
        {yMax}
      </text>
      <text
        x={GUTTER_LEFT - 6}
        y={OCC_HEIGHT}
        textAnchor="end"
        className="fill-onlight-text-3 dark:fill-ondark-text-3"
        fontSize={10}
      >
        0
      </text>

      {/* unobserved buckets as a faint band */}
      {occupancy.map((b, i) =>
        b.observedSeconds === 0 ? (
          <rect
            key={b.bucketStartUtc}
            x={GUTTER_LEFT + i * slot}
            y={0}
            width={slot}
            height={OCC_HEIGHT}
            className="fill-onlight-bg-3 dark:fill-ondark-bg-3"
            opacity={0.6}
          >
            <title>{`${fmt(b.bucketStartUtc, 'HH:mm')} — no clips`}</title>
          </rect>
        ) : null
      )}

      {/* observed buckets: area under the step, for hover */}
      {occupancy.map((b, i) =>
        b.observedSeconds > 0 ? (
          <rect
            key={b.bucketStartUtc}
            x={GUTTER_LEFT + i * slot}
            y={yFor(b.max)}
            width={slot}
            height={OCC_HEIGHT - yFor(b.max)}
            className="fill-teal-600"
            opacity={0.18}
          >
            <title>{`${fmt(b.bucketStartUtc, 'HH:mm')} — peak ${b.max}, mean ${
              b.mean
            }, ${b.clips} clip${b.clips === 1 ? '' : 's'}, ${
              b.observedSeconds
            }s observed`}</title>
          </rect>
        ) : null
      )}

      {/* threshold */}
      {threshold != null && threshold > 0 && (
        <>
          <line
            x1={GUTTER_LEFT}
            x2={WIDTH - GUTTER_RIGHT}
            y1={yFor(threshold)}
            y2={yFor(threshold)}
            className="stroke-red-500"
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
          <text
            x={WIDTH - GUTTER_RIGHT}
            y={yFor(threshold) - 4}
            textAnchor="end"
            className="fill-red-500"
            fontSize={10}
          >
            limit {threshold}
          </text>
        </>
      )}

      {/* the step line */}
      {stepPath && (
        <path
          d={stepPath}
          fill="none"
          className="stroke-teal-600 dark:stroke-teal-400"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      )}

      {/* time axis */}
      <line
        x1={GUTTER_LEFT}
        x2={WIDTH - GUTTER_RIGHT}
        y1={OCC_HEIGHT + 0.5}
        y2={OCC_HEIGHT + 0.5}
        className="stroke-onlight-line-2 dark:stroke-ondark-line-2"
      />
      {occupancy.map((b, i) => {
        if (i % labelEvery !== 0) return null;
        const x = GUTTER_LEFT + i * slot;
        // The svg clips at WIDTH, so a label that would run past the
        // right edge is drawn ending at its bucket instead of starting there.
        const overflows = x + LABEL_WIDTH > WIDTH;
        return (
          <text
            key={b.bucketStartUtc}
            x={x}
            y={OCC_HEIGHT + 13}
            textAnchor={overflows ? 'end' : 'start'}
            className="fill-onlight-text-3 dark:fill-ondark-text-3"
            fontSize={10}
          >
            {fmt(b.bucketStartUtc, 'HH:mm')}
          </text>
        );
      })}
    </svg>
  );
}

export default OccupancyChart;
